import { Component, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { SessionService } from '@app/core/services/session/session.service';
import { FieldOption } from '@app/shared/models/field.interface';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { GetHierarchyDto } from '../../models/hierarchy.model';
import { HierarchyService } from '../../services/hierarchy.service';

@Component({
  selector: 'vfs-hierarchy-select',
  templateUrl: './hierarchy-select.component.html',
  styleUrls: ['./hierarchy-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => HierarchySelectComponent)
    }
  ]
})
export class HierarchySelectComponent implements OnInit, ControlValueAccessor {
  hierarchies$: Observable<GetHierarchyDto[]>;
  hierarchies: GetHierarchyDto[];
  hierarchyOptions: FieldOption[];
  @Input() formControl: FormControl;
  @Input() ngModel: GetHierarchyDto;
  @Input() hierarchyType: any;
  @Input() required: boolean;
  @Input() disabled: boolean;
  @Output() ngModelChange = new EventEmitter<GetHierarchyDto>();
  @Output() changeValue = new EventEmitter<GetHierarchyDto>();
  @ViewChild(MatSelect) matSelect: MatSelect;

  // ControlValueAccessor
  value: any;

  constructor(protected hierarchyService: HierarchyService, protected sessionService: SessionService) {}

  ngOnInit() {
    this.hierarchies$ = this.hierarchyService.getAllHierarchiesWithoutDivisions().pipe(
      tap(hierarchies => {
        this.hierarchies = hierarchies.filter(
          hierarchy => hierarchy.type.toLowerCase() === this.hierarchyType.toLowerCase()
        );
        this.ngModel = hierarchies.find(hierarchy => hierarchy.id === this.ngModel.id);
        this.hierarchyOptions = this.getHierarchyOptions(this.hierarchies);
      })
    );
  }

  public getHierarchyOptions(hierarchies: GetHierarchyDto[]): FieldOption[] {
    return hierarchies.map(hierarchy => ({
      label: hierarchy.description,
      value: hierarchy,
      disabled: !this.checkHierarchyDivisions(hierarchy)
    }));
  }

  public change(event: MatSelectChange) {
    this.changeValue.emit(event.value);
    if (this.ngModel && !this.formControl) {
      this.ngModel = event.value;
      this.ngModelChange.emit(this.ngModel);
    }
  }

  ngModelIsValid(): boolean {
    return (
      this.matSelect &&
      this.matSelect.ngControl &&
      (this.matSelect.ngControl.valid || this.matSelect.ngControl.disabled)
    );
  }

  checkHierarchyDivisions(hierarchy: GetHierarchyDto): boolean {
    const divisions: string[] = this.sessionService
      .getUser()
      .userDivisions.map(ud => (ud.division.active ? ud.division.id : null))
      .filter(d => d !== null);
    const hierarchyDivisions: string[] = hierarchy.divisions
      .map(ud => (ud.division.active ? ud.division.id : null))
      .filter(d => d !== null);
    return divisions.some(div => hierarchyDivisions.includes(div));
  }

  // ControlValueAccessor functions
  onChange = (rating: number) => {};
  onTouched = () => {};

  writeValue(obj: any): void {
    this.value = obj;
  }
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
